import type { RBACGroup, RBACUser } from '../../types';

// Pure logic for the Copy Roles flow: narrowing the reference-user candidate list
// (region / department / job-title) and deriving which of a reference user's roles get
// applied to the primary user. No Graph and no React — see features/users/components/
// CopyRoles for the UI and graphApi.getUserGroupMembership for the role data.

// The operator-facing region options. `country` in Entra is free text, so each region
// matches a set of tolerant variants rather than one exact value — the values a tenant
// actually stores should be confirmed on first real run, and unmatched countries are
// surfaced by regionOf() returning undefined (so nothing is silently misfiled).
export type RegionCode = 'NZ' | 'AUS' | 'US' | 'NDL';

export const REGIONS: { code: RegionCode; label: string; matches: string[] }[] = [
  { code: 'NZ', label: 'New Zealand', matches: ['new zealand', 'nz', 'nzl'] },
  { code: 'AUS', label: 'Australia', matches: ['australia', 'aus', 'au'] },
  {
    code: 'US',
    label: 'United States',
    matches: ['united states', 'united states of america', 'usa', 'us'],
  },
  {
    code: 'NDL',
    label: 'Netherlands',
    matches: ['netherlands', 'the netherlands', 'holland', 'ndl', 'nl', 'nld'],
  },
];

// The region a free-text country value maps to, or undefined if it matches none of the
// known regions. The single lookup that regionOf (code) and userRegionDepartment (label)
// both read from, so the match rule lives in one place.
const regionFor = (country: string | undefined): (typeof REGIONS)[number] | undefined => {
  const normalized = (country ?? '').trim().toLowerCase();
  if (!normalized) return undefined;
  return REGIONS.find((region) => region.matches.includes(normalized));
};

// The RegionCode a free-text country value maps to, or undefined if it matches none of
// the known regions (caller can surface those so a mapping gap is visible, not silent).
export const regionOf = (country: string | undefined): RegionCode | undefined =>
  regionFor(country)?.code;

// A one-line "region · department" summary for a user, used by the Copy Roles header to
// show who roles are being assigned to. Region uses the mapped label, falling back to the
// raw country when it maps to no known region; empty parts are dropped.
export const userRegionDepartment = (user: RBACUser): string => {
  const regionLabel = regionFor(user.country)?.label ?? user.country ?? '';
  return [regionLabel, user.department].filter(Boolean).join(' · ');
};

// Distinct, non-empty department values across the given users, sorted — drives the
// Department dropdown, derived from real data rather than a hardcoded list (departments
// vary per tenant and change over time). Deduped case-insensitively (keeping the first
// casing seen) so it matches filterReferenceCandidates' case-insensitive comparison —
// otherwise 'Sales' and 'sales' would list as two options that filter identically.
export const departmentOptions = (users: RBACUser[]): string[] => {
  const byLower = new Map<string, string>();
  for (const user of users) {
    const department = (user.department ?? '').trim();
    if (department && !byLower.has(department.toLowerCase())) {
      byLower.set(department.toLowerCase(), department);
    }
  }
  return Array.from(byLower.values()).sort((a, b) => a.localeCompare(b));
};

export type ReferenceFilter = {
  region?: RegionCode;
  department?: string;
  // Matches display name OR job title, case-insensitive substring.
  query?: string;
};

// Narrows the full user list to reference-user candidates. Excludes the primary user
// (can't copy from yourself) and disabled accounts (a peer to copy from should be
// active). Region and department, when set, must match; an empty query lists everyone
// left in scope (the "region fallback" — see issue #104), a non-empty one matches the
// display name or job title. Result is sorted by display name.
export const filterReferenceCandidates = (
  users: RBACUser[],
  primaryUserId: string,
  { region, department, query }: ReferenceFilter
): RBACUser[] => {
  const q = (query ?? '').trim().toLowerCase();
  const wantDepartment = (department ?? '').trim().toLowerCase();

  return users
    .filter(
      (user) =>
        user.id !== primaryUserId &&
        user.accountEnabled &&
        (!region || regionOf(user.country) === region) &&
        (!wantDepartment || (user.department ?? '').trim().toLowerCase() === wantDepartment) &&
        (!q ||
          user.displayName.toLowerCase().includes(q) ||
          (user.jobTitle ?? '').toLowerCase().includes(q))
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

// The ids of the reference user's roles that are copyable — i.e. every role the peer holds
// that the primary doesn't already have. This is the "everything new" set that both the
// default selection and the "Select all" action stage. Single source of truth so the two
// can't drift on what counts as copyable.
export const copyableIds = (
  referenceRoles: RBACGroup[],
  primaryRoles: RBACGroup[]
): Set<string> => {
  const alreadyHeld = new Set(primaryRoles.map((role) => role.id));
  return new Set(referenceRoles.filter((role) => !alreadyHeld.has(role.id)).map((role) => role.id));
};

// The roles that will actually be added to the primary user: the reference user's roles
// the operator selected, minus any the primary already holds (re-applying is a no-op, so
// they're never written). Keyed by group id. Preserves the reference list's order.
export const deriveRolesToCopy = (
  referenceRoles: RBACGroup[],
  primaryRoles: RBACGroup[],
  selectedIds: ReadonlySet<string>
): RBACGroup[] => {
  // Reuse copyableIds so "what counts as copyable" (not already held) is defined once: a
  // selected reference role is copied iff it's in that set. Order follows referenceRoles.
  const copyable = copyableIds(referenceRoles, primaryRoles);
  return referenceRoles.filter((role) => selectedIds.has(role.id) && copyable.has(role.id));
};
